import { useState, useEffect } from 'react';
import { Copy, CheckCircle, RefreshCw, Shield } from 'lucide-react';
import { toast } from 'react-hot-toast';
import connectorService from '../../services/connector.service';
import type { ConnectorWireguard } from '../../services/connector.service';

interface WireGuardTabProps {
  connectorId: string;
}

const WireGuardTab = ({ connectorId }: WireGuardTabProps) => {
  const [wg, setWg] = useState<ConnectorWireguard | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [editData, setEditData] = useState({
    listenPort: 51820,
    mtu: 1420,
    endpointHost: '',
    enabled: true,
  });

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const data = await connectorService.getWireguard(connectorId);
      setWg(data);
      setEditData({
        listenPort: data.listen_port,
        mtu: data.mtu || 1420,
        endpointHost: data.endpoint_host || '',
        enabled: data.enabled === 1,
      });
    } catch (error: any) {
      toast.error('Failed to load WireGuard config');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, [connectorId]);

  const handleSave = async () => {
    try {
      setSaving(true);
      await connectorService.updateWireguard(connectorId, editData);
      toast.success('WireGuard config updated');
      fetchConfig();
    } catch (error: any) {
      toast.error('Failed to update WireGuard config');
    } finally {
      setSaving(false);
    }
  };

  const handleRegenerateKeys = async () => {
    if (!confirm('Regenerating keys will require updating all peers. Continue?')) return;
    try {
      setRegenerating(true);
      await connectorService.regenerateKeys(connectorId);
      toast.success('Keys regenerated');
      fetchConfig();
    } catch (error: any) {
      toast.error('Failed to regenerate keys');
    } finally {
      setRegenerating(false);
    }
  };

  const copyPublicKey = async () => {
    if (!wg?.public_key) return;
    await navigator.clipboard.writeText(wg.public_key);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" /></div>;
  }

  if (!wg) {
    return <div className="text-center py-12 text-gray-500">No WireGuard config found</div>;
  }

  return (
    <div className="space-y-6">
      {/* Public Key */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center space-x-2">
          <Shield className="w-5 h-5 text-primary-600" />
          <span>Public Key</span>
        </h3>
        <div className="flex items-center space-x-3">
          <code className="flex-1 bg-gray-100 rounded px-3 py-2 text-sm font-mono break-all">
            {wg.public_key}
          </code>
          <button onClick={copyPublicKey} className="btn-secondary inline-flex items-center space-x-1">
            {copied ? <CheckCircle className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
            <span>{copied ? 'Copied' : 'Copy'}</span>
          </button>
        </div>
        <div className="mt-3">
          <button
            onClick={handleRegenerateKeys}
            disabled={regenerating}
            className="text-sm text-orange-600 hover:text-orange-700 inline-flex items-center space-x-1"
          >
            <RefreshCw className={`w-3 h-3 ${regenerating ? 'animate-spin' : ''}`} />
            <span>{regenerating ? 'Regenerating...' : 'Regenerate Keys'}</span>
          </button>
        </div>
      </div>

      {/* Config */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Interface Settings</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Listen Port</label>
            <input
              type="number"
              value={editData.listenPort}
              onChange={(e) => setEditData(d => ({ ...d, listenPort: parseInt(e.target.value) || 51820 }))}
              className="input-base w-full"
              min={1024}
              max={65535}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Server Address</label>
            <code className="block bg-gray-100 rounded px-3 py-2 text-sm font-mono">
              {wg.server_address || 'Not assigned'}
            </code>
            <p className="mt-1 text-xs text-gray-500">Automatically assigned from hub edge subnet</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">MTU</label>
            <input
              type="number"
              value={editData.mtu}
              onChange={(e) => setEditData(d => ({ ...d, mtu: parseInt(e.target.value) || 1420 }))}
              className="input-base w-full"
              min={1280}
              max={9000}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Endpoint Host</label>
            <input
              type="text"
              value={editData.endpointHost}
              onChange={(e) => setEditData(d => ({ ...d, endpointHost: e.target.value }))}
              className="input-base w-full"
              placeholder="Public hostname or IP"
            />
          </div>
          <div className="flex items-center space-x-3">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={editData.enabled}
                onChange={(e) => setEditData(d => ({ ...d, enabled: e.target.checked }))}
                className="w-4 h-4 text-primary-600 border-gray-300 rounded"
              />
              <span className="text-sm font-medium text-gray-700">Enabled</span>
            </label>
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="btn-primary"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WireGuardTab;
